function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import { campaignsDashboardOnly, autopilotHiddenHere } from "../../functions/tenant-features";
import { Link } from "react-router-dom";
import { PageHeader, Switcher } from "../../components/analytics-kit";
import { autopilotSvc } from "./autopilot-common";

/*
 * Campaigns section navigation (CAMPAIGNS_DASHBOARD_REDESIGN.md §2):
 * Overview · Markets · Flights, plus Settings. Rendered by every campaigns
 * screen so the section reads as one place. Dashboard-only (managed, read-only)
 * tenants see Overview + Markets only — Flights is dropped.
 */
export const CAMPAIGN_TABS = [["overview", "Overview", "/Campaigns"], ["markets", "Markets", "/Campaigns/Markets"], ["flights", "Flights", "/Campaigns/Flights"]];
export default function CampaignsNav({
  active,
  history,
  subtitle,
  chips,
  actions,
  title = "Campaigns"
}) {
  // Managed (dashboards-only) tenants keep Flights hidden but DO get Autopilot (2026-09-27):
  // it drafts for them and "Request launch" hands the draft to their Plunk team via Campaign Review.
  const tabs = campaignsDashboardOnly() ? CAMPAIGN_TABS.filter(([k]) => k !== "flights") : CAMPAIGN_TABS;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(PageHeader, {
    title: title,
    subtitle: subtitle,
    chips: chips || [],
    actions: actions
  }), /*#__PURE__*/React.createElement(Switcher, {
    ariaLabel: "Campaigns views",
    items: tabs.map(([key, label, path]) => ({
      to: path,
      label,
      isActive: () => active === key
    }))
  }));
}

// Ribbon delta in the kit's shape ({dir, pct, unit}); pp = percentage-point difference (CTR).
export function ribbonDelta(cur, prev, opts = {}) {
  if (prev === undefined || prev === null) return null;
  const c = Number(cur) || 0,
    p = Number(prev) || 0;
  if (opts.pp) {
    if (!p) return null;
    const d = c - p;
    return {
      dir: d > 0 ? "up" : d < 0 ? "down" : "flat",
      pct: d,
      unit: "pp"
    };
  }
  if (p < MIN_PRIOR) return null;
  const d = (c - p) / p * 100;
  return {
    dir: d > 0 ? "up" : d < 0 ? "down" : "flat",
    pct: d
  };
}

// Δ helpers shared by Overview and Markets.
// Noise floor (same rule as Sales/Contacts/Product performance): a prior under
// 10 makes the % meaningless (+13,296.7% off a handful of impressions), so no
// chip. Above the floor, a change past 10x reads as "over 10x" instead of a
// five-digit percent.
const MIN_PRIOR = 10;
export function delta(cur, prev) {
  const c = Number(cur) || 0,
    p = Number(prev) || 0;
  if (p < MIN_PRIOR) return null;
  return (c - p) / p * 100;
}
function pctText(d) {
  return d > 900 ? "over 10x" : `${Math.abs(d).toFixed(1)}%`;
}
export function DeltaTag({
  cur,
  prev,
  invert = false,
  pp = false
}) {
  if (prev === undefined || prev === null) return null;
  const c = Number(cur) || 0,
    p = Number(prev) || 0;
  if (pp) {
    const d = c - p;
    if (!p && !c) return null;
    const good = invert ? d <= 0 : d >= 0;
    return /*#__PURE__*/React.createElement("span", {
      className: "kpi-delta",
      style: {
        fontSize: 11.5,
        fontWeight: 600,
        color: good ? "var(--plk-good)" : "var(--plk-bad)"
      }
    }, d >= 0 ? "▲" : "▼", " ", Math.abs(d).toFixed(2), " pp");
  }
  const d = delta(c, p);
  if (d === null) return null;
  const good = invert ? d <= 0 : d >= 0;
  return /*#__PURE__*/React.createElement("span", {
    className: "kpi-delta",
    style: {
      fontSize: 11.5,
      fontWeight: 600,
      color: good ? "var(--plk-good)" : "var(--plk-bad)"
    }
  }, d >= 0 ? "▲" : "▼", " ", pctText(d));
}
export const COMPARE_OPTS = [["previous_period", "vs previous period"], ["previous_year", "vs previous year"], ["none", "no comparison"]];

// KPI tile delta in the shape components/card Kpi expects: {text, dir}
export function kpiDelta(cur, prev, opts = {}) {
  if (prev === undefined || prev === null) return null;
  const c = Number(cur) || 0,
    p = Number(prev) || 0;
  if (opts.pp) {
    if (!p && !c) return null;
    const d = c - p;
    const good = opts.invert ? d <= 0 : d >= 0;
    return {
      text: `${Math.abs(d).toFixed(2)} pp`,
      dir: d === 0 ? "flat" : good ? "up" : "down"
    };
  }
  const d = delta(c, p);
  if (d === null) return null;
  const good = opts.invert ? d <= 0 : d >= 0;
  return {
    text: pctText(d),
    dir: d === 0 ? "flat" : good ? "up" : "down"
  };
}

// Tenant-facing campaign title: the campaign table name when the server had it,
// else the stats name with the account prefix ("a316487212239__") and
// underscores cleaned up.
export function campaignTitle(c) {
  const raw = c && (c.name || c.statsName) || "";
  return String(raw).replace(/^a\d+_+/, "").replace(/_+/g, " ").trim();
}

/** Campaigns keeps a one-line Autopilot strip (its counts for the campaign lane) that deep-links to the section. */
export class AutopilotStrip extends React.Component {
  constructor(...args) {
    super(...args);
    _defineProperty(this, "state", {
      st: undefined
    });
  }
  componentDidMount() {
    this._alive = true;
    if (autopilotHiddenHere()) return;
    autopilotSvc().getAsync().then(s => this._alive && this.setState({
      st: s && typeof s === "object" ? s : null
    })).catch(() => this._alive && this.setState({
      st: null
    }));
  }
  componentWillUnmount() {
    this._alive = false;
  }
  render() {
    const {
      st
    } = this.state;
    if (!st || autopilotHiddenHere()) return null;
    const s = st.summary || {};
    // Same word as the Autopilot page ("Paused"), which this used to call "Off".
    const bits = [st.enabled ? "Running" : "Paused"];
    if (Number(s.approvalsPending) > 0) bits.push(`${s.approvalsPending} awaiting your approval`);
    if (Number(s.openRecommendations) > 0) bits.push(`${s.openRecommendations} open recommendation${Number(s.openRecommendations) === 1 ? "" : "s"}`);
    if (Number(s.activeCampaigns) > 0) bits.push(`${s.activeCampaigns} Autopilot campaign${Number(s.activeCampaigns) === 1 ? "" : "s"} live`);
    return /*#__PURE__*/React.createElement("div", {
      className: "wa-notice"
    }, /*#__PURE__*/React.createElement("b", null, "Autopilot"), " \xB7 ", bits.join(" · "), " \xB7 ", /*#__PURE__*/React.createElement(Link, {
      to: "/Autopilot/campaigns"
    }, "Open Autopilot \u2192"));
  }
}